import React, { useState, useEffect, useMemo } from 'react';
import {
  Button,
  TextField,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Switch,
  Checkbox,
  IconButton,
  Tooltip,
  Box,
  InputAdornment,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
import {
  getFeatureCoursesView,
  saveCourseFeatureBulk,
  removeCourseFeatureBulk,
  saveCourseFeature,
} from '../core/featureCourses';
import { validate as uuidValidate } from 'uuid';
import moment from 'moment';
import styles from './FeatureCourses.module.css';

const FEATURES = ['search_across_sections'];

const FeatureCourses = () => {
  const [data, setData] = useState([]);
  const [selectedFeature, setSelectedFeature] = useState(FEATURES[0]);
  const [courseIdsInput, setCourseIdsInput] = useState('');
  const [orderBy, setOrderBy] = useState('updatedAt');
  const [order, setOrder] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRows, setSelectedRows] = useState([]);

  const fetchData = async () => {
    try {
      const result = await getFeatureCoursesView();
      setData(Array.isArray(result) ? result : []);
    } catch (error) {
      console.error('Failed to fetch feature courses', error);
      setData([]);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedData = useMemo(() => {
    if (!Array.isArray(data)) return [];
    const comparator = (a, b) => {
      let valA = a[orderBy];
      let valB = b[orderBy];

      if (orderBy === 'updatedAt' || orderBy === 'createdAt') {
        valA = new Date(valA);
        valB = new Date(valB);
      }

      if (valB < valA) return order === 'desc' ? -1 : 1;
      if (valB > valA) return order === 'desc' ? 1 : -1;
      return 0;
    };
    return [...data].sort(comparator);
  }, [data, order, orderBy]);

  const filteredData = useMemo(() => {
    if (!Array.isArray(sortedData)) return [];
    if (!searchTerm) return sortedData;
    const lowerSearch = searchTerm.toLowerCase();
    return sortedData.filter(
      (row) =>
        row.courseId.toLowerCase().includes(lowerSearch) ||
        row.courseName.toLowerCase().includes(lowerSearch) ||
        row.feature.toLowerCase().includes(lowerSearch) ||
        row.createdByName.toLowerCase().includes(lowerSearch) ||
        row.updatedByName.toLowerCase().includes(lowerSearch)
    );
  }, [sortedData, searchTerm]);

  const handleToggleEnabled = async (row) => {
    try {
      await saveCourseFeature(row.courseId, row.feature, !row.enabled);
      await fetchData();
    } catch (error) {
      alert(`Failed to update feature: ${error}`);
    }
  };

  const parseCourseIds = () => {
    return courseIdsInput
      .split(',')
      .map((id) => id.trim())
      .filter((id) => id && uuidValidate(id));
  };

  const handleBulkAction = async (action) => {
    const courseIds = parseCourseIds();
    if (courseIds.length === 0 && selectedRows.length === 0) {
      alert('Please enter valid Course IDs or select rows');
      return;
    }

    const featureCourses =
      courseIds.length > 0
        ? courseIds.map((courseId) => ({
            courseId,
            feature: selectedFeature,
          }))
        : selectedRows.map((id) => {
            const row = data.find((r) => r.id === id);
            return {
              courseId: row.courseId,
              feature: row.feature,
            };
          });

    try {
      if (action === 'enable') {
        await saveCourseFeatureBulk(featureCourses, true);
      } else if (action === 'disable') {
        await saveCourseFeatureBulk(featureCourses, false);
      } else if (action === 'remove') {
        await removeCourseFeatureBulk(featureCourses);
      }
      await fetchData();
      setCourseIdsInput('');
      setSelectedRows([]);
    } catch (error) {
      alert(`Bulk action ${action} failed: ${error.message}`);
    }
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = filteredData.map((n) => n.id);
      setSelectedRows(newSelecteds);
      return;
    }
    setSelectedRows([]);
  };

  const handleClick = (_event, id) => {
    const selectedIndex = selectedRows.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedRows, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedRows.slice(1));
    } else if (selectedIndex === selectedRows.length - 1) {
      newSelected = newSelected.concat(selectedRows.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedRows.slice(0, selectedIndex),
        selectedRows.slice(selectedIndex + 1)
      );
    }
    setSelectedRows(newSelected);
  };

  const isBulkActionsEnabled = courseIdsInput.length > 0;

  return (
    <Box p={4}>
      <Typography variant="h4" gutterBottom>
        Feature Course Management
      </Typography>

      <Paper style={{ padding: 20, marginBottom: 20 }}>
        <Typography variant="h6" gutterBottom>
          Bulk Actions
        </Typography>
        <Box display="flex" alignItems="center" gridGap={20} flexWrap="wrap">
          <TextField
            select
            label="Feature Name"
            value={selectedFeature}
            onChange={(e) => setSelectedFeature(e.target.value)}
            SelectProps={{ native: true }}
            variant="outlined"
            size="small"
            style={{ minWidth: 200 }}
          >
            {FEATURES.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </TextField>

          <TextField
            label="Course IDs (comma separated)"
            value={courseIdsInput}
            onChange={(e) => setCourseIdsInput(e.target.value)}
            variant="outlined"
            size="small"
            style={{ flexGrow: 1 }}
            placeholder="e.g. uuid1, uuid2"
          />

          <Button
            variant="contained"
            color="primary"
            onClick={() => handleBulkAction('enable')}
            disabled={!isBulkActionsEnabled}
          >
            Enable
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => handleBulkAction('disable')}
            disabled={!isBulkActionsEnabled}
          >
            Disable
          </Button>
          <Button
            variant="outlined"
            className={styles.removeButton}
            onClick={() => handleBulkAction('remove')}
            disabled={!isBulkActionsEnabled}
          >
            Remove
          </Button>
        </Box>
      </Paper>

      <Paper>
        <Box
          p={2}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <TextField
            placeholder="Search table..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            variant="outlined"
            size="small"
            style={{ width: 400 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchTerm('')}
                    aria-label="clear search"
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Box>
            {selectedRows.length > 0 && (
              <Typography
                variant="subtitle1"
                component="span"
                style={{ marginRight: 20 }}
              >
                {selectedRows.length} selected
              </Typography>
            )}
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={() => handleBulkAction('enable')}
              disabled={selectedRows.length === 0}
              style={{ marginRight: 10 }}
            >
              Enable
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              onClick={() => handleBulkAction('disable')}
              disabled={selectedRows.length === 0}
              style={{ marginRight: 10 }}
            >
              Disable
            </Button>
            <Button
              size="small"
              variant="outlined"
              className={styles.removeButton}
              onClick={() => handleBulkAction('remove')}
              disabled={selectedRows.length === 0}
            >
              Remove
            </Button>
          </Box>
        </Box>

        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={
                      selectedRows.length > 0 &&
                      selectedRows.length < filteredData.length
                    }
                    checked={
                      filteredData.length > 0 &&
                      selectedRows.length === filteredData.length
                    }
                    onChange={handleSelectAllClick}
                  />
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'courseId'}
                    direction={orderBy === 'courseId' ? order : 'asc'}
                    onClick={() => handleRequestSort('courseId')}
                  >
                    Course ID
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'courseName'}
                    direction={orderBy === 'courseName' ? order : 'asc'}
                    onClick={() => handleRequestSort('courseName')}
                  >
                    Course Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'feature'}
                    direction={orderBy === 'feature' ? order : 'asc'}
                    onClick={() => handleRequestSort('feature')}
                  >
                    Feature
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'enabled'}
                    direction={orderBy === 'enabled' ? order : 'asc'}
                    onClick={() => handleRequestSort('enabled')}
                  >
                    Enabled
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'createdAt'}
                    direction={orderBy === 'createdAt' ? order : 'asc'}
                    onClick={() => handleRequestSort('createdAt')}
                  >
                    Created
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'updatedAt'}
                    direction={orderBy === 'updatedAt' ? order : 'asc'}
                    onClick={() => handleRequestSort('updatedAt')}
                  >
                    Updated
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'createdBy'}
                    direction={orderBy === 'createdBy' ? order : 'asc'}
                    onClick={() => handleRequestSort('createdBy')}
                  >
                    Creator
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'updatedBy'}
                    direction={orderBy === 'updatedBy' ? order : 'asc'}
                    onClick={() => handleRequestSort('updatedBy')}
                  >
                    Updater
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.length > 0 ? (
                filteredData.map((row) => {
                  const isItemSelected = selectedRows.includes(row.id);
                  return (
                    <TableRow
                      key={row.id}
                      hover
                      onClick={(event) => handleClick(event, row.id)}
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      selected={isItemSelected}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox checked={isItemSelected} />
                      </TableCell>
                      <TableCell>{row.courseId}</TableCell>
                      <TableCell>{row.courseName}</TableCell>
                      <TableCell>{row.feature}</TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Switch
                          checked={row.enabled}
                          onChange={() => handleToggleEnabled(row)}
                          color="primary"
                        />
                      </TableCell>
                      <TableCell>
                        {moment(row.createdAt).format('YYYY-MM-DD HH:mm')}
                      </TableCell>
                      <TableCell>
                        {moment(row.updatedAt).format('YYYY-MM-DD HH:mm')}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={row.createdBy || 'created_by is null'}>
                          <span>{row.createdByName}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={row.updatedBy || 'updated_by is null'}>
                          <span>{row.updatedByName}</span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={9} className={styles.noData}>
                    No data found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default FeatureCourses;
